import { useState } from 'react';
import {
  anniversaryTypeConfigs,
  anniversaries,
  anniversaryStats,
  anniversaryRules,
  getTodayAnniversaries,
  getThisWeekAnniversaries,
  getTypeDistribution,
  getMostCelebrated,
  type AnniversaryEntityType,
} from '@/mocks/rfwAnniversaries';

export default function AniversariosSection() {
  const [activeTab, setActiveTab] = useState<'hoy' | 'semana' | 'tipos' | 'reglas'>('hoy');
  const [selectedType, setSelectedType] = useState<AnniversaryEntityType | 'todas'>('todas');

  const todayList = getTodayAnniversaries();
  const weekList = getThisWeekAnniversaries();
  const typeDist = getTypeDistribution();
  const topCelebrated = getMostCelebrated(5);

  const filteredAnniversaries = (() => {
    let items = activeTab === 'hoy' ? todayList : weekList;
    if (selectedType !== 'todas') {
      items = items.filter((a) => a.entityType === selectedType);
    }
    return items.slice(0, 8);
  })();

  return (
    <section className="py-14 md:py-20 bg-background-50">
      <div className="max-w-6xl mx-auto px-4 md:px-6">
        {/* Header */}
        <div className="text-center mb-8">
          <div className="inline-flex items-center gap-2 px-3 py-1 rounded-full bg-rose-100 border border-rose-200 mb-4">
            <span className="w-2 h-2 rounded-full bg-rose-500 animate-pulse"></span>
            <span className="text-xs font-semibold text-rose-700 tracking-wide">SISTEMA DE ANIVERSARIOS</span>
          </div>
          <h2 className="text-2xl md:text-4xl font-bold text-foreground-950 mb-3">
            Hoy se <span className="bg-gradient-to-r from-rose-500 via-violet-500 to-amber-500 bg-clip-text text-transparent">celebra</span> en RFW
          </h2>
          <p className="text-foreground-600 text-sm md:text-base max-w-xl mx-auto">
            Radios, programas, locutores, clubes y oyentes que cumplen años en la comunidad. Todo automático. Vos solo tenés que felicitar, comentar y compartir.
          </p>
        </div>

        {/* Stats Bar */}
        <div className="grid grid-cols-2 md:grid-cols-4 gap-2 md:gap-3 mb-8">
          {[
            { value: anniversaryStats.totalToday.toString(), label: 'Hoy', icon: 'ri-gift-line', color: 'text-rose-600', bg: 'bg-rose-50' },
            { value: anniversaryStats.totalThisWeek.toString(), label: 'Esta Semana', icon: 'ri-calendar-check-line', color: 'text-violet-600', bg: 'bg-violet-50' },
            { value: anniversaryStats.totalThisMonth.toString(), label: 'Este Mes', icon: 'ri-calendar-line', color: 'text-amber-600', bg: 'bg-amber-50' },
            { value: anniversaryStats.totalFelicitacionesHoy.toLocaleString(), label: 'Felicitaciones Hoy', icon: 'ri-heart-line', color: 'text-emerald-600', bg: 'bg-emerald-50' },
          ].map((stat, i) => (
            <div key={i} className={`${stat.bg} rounded-lg p-3 text-center`}>
              <div className={`text-lg font-bold ${stat.color}`}>{stat.value}</div>
              <div className="flex items-center justify-center gap-1 text-xs text-foreground-500 mt-1">
                <i className={`${stat.icon} text-xs`}></i>
                <span>{stat.label}</span>
              </div>
            </div>
          ))}
        </div>

        {/* Tabs */}
        <div className="flex items-center justify-center mb-7">
          <div className="inline-flex bg-background-100 rounded-full p-1">
            {(['hoy', 'semana', 'tipos', 'reglas'] as const).map((tab) => (
              <button
                key={tab}
                onClick={() => setActiveTab(tab)}
                className={`px-4 py-2 rounded-full text-sm font-medium whitespace-nowrap transition-all cursor-pointer ${
                  activeTab === tab
                    ? 'bg-background-50 text-foreground-950 shadow-sm'
                    : 'text-foreground-500 hover:text-foreground-700'
                }`}
              >
                {tab === 'hoy' && 'Hoy'}
                {tab === 'semana' && 'Esta Semana'}
                {tab === 'tipos' && 'Tipos'}
                {tab === 'reglas' && 'Reglas'}
              </button>
            ))}
          </div>
        </div>

        {/* Tab: Hoy */}
        {activeTab === 'hoy' && (
          <div>
            {todayList.length > 0 && (
              <div className="mb-5 p-4 bg-gradient-to-r from-rose-50 via-violet-50 to-amber-50 border border-rose-200 rounded-xl text-center">
                <div className="flex items-center justify-center gap-2 mb-1">
                  <i className="ri-gift-fill text-rose-500 text-lg"></i>
                  <span className="text-sm font-semibold text-rose-700">¡{todayList.length} aniversarios hoy!</span>
                </div>
                <div className="flex flex-wrap items-center justify-center gap-1.5 text-xs text-rose-600">
                  {todayList.map((a) => (
                    <span key={a.id} className="font-medium">{a.entityName}{a !== todayList[todayList.length - 1] ? ',' : ''}</span>
                  ))}
                  <span>— ¡andá a felicitar!</span>
                </div>
              </div>
            )}

            <div className="flex flex-wrap items-center justify-center gap-1.5 mb-6">
              <span className="text-xs text-foreground-500 mr-1">Filtrar:</span>
              <button
                onClick={() => setSelectedType('todas')}
                className={`px-2.5 py-1 rounded-full text-xs font-medium whitespace-nowrap transition-all cursor-pointer ${
                  selectedType === 'todas' ? 'bg-rose-500 text-white' : 'bg-background-100 text-foreground-600 hover:bg-background-200'
                }`}
              >
                Todas
              </button>
              {anniversaryTypeConfigs.map((cfg) => (
                <button
                  key={cfg.key}
                  onClick={() => setSelectedType(cfg.key)}
                  className={`px-2.5 py-1 rounded-full text-xs font-medium whitespace-nowrap transition-all cursor-pointer flex items-center gap-1 ${
                    selectedType === cfg.key ? `${cfg.bgColor} ${cfg.color} border ${cfg.borderColor}` : 'bg-background-100 text-foreground-600 hover:bg-background-200'
                  }`}
                >
                  <i className={`${cfg.icon}`}></i>
                  {cfg.label}
                </button>
              ))}
            </div>

            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              {filteredAnniversaries.map((ann) => {
                const typeCfg = anniversaryTypeConfigs.find((t) => t.key === ann.entityType);
                return (
                  <div key={ann.id} className={`bg-background-50 rounded-lg p-4 border ${typeCfg?.borderColor ?? 'border-background-200/70'} hover:border-rose-200 transition-colors`}>
                    <div className="flex items-center gap-3 mb-3">
                      <div className="relative w-14 h-14 rounded-xl overflow-hidden flex-shrink-0">
                        <img src={ann.entityPhoto} alt={ann.entityName} className="w-full h-full object-cover" />
                        {ann.isToday && (
                          <div className="absolute -top-1 -right-1 w-6 h-6 bg-rose-500 rounded-full border-2 border-background-50 flex items-center justify-center">
                            <i className="ri-gift-fill text-white text-xs"></i>
                          </div>
                        )}
                      </div>
                      <div className="flex-1 min-w-0">
                        <div className="font-semibold text-sm text-foreground-950 truncate">{ann.entityName}</div>
                        <div className={`inline-flex items-center gap-1 px-1.5 py-0.5 rounded-full text-xs font-medium ${typeCfg?.bgColor ?? 'bg-background-100'} ${typeCfg?.color ?? 'text-foreground-600'}`}>
                          <i className={`${typeCfg?.icon ?? 'ri-question-line'} text-xs`}></i>
                          {typeCfg?.label ?? ann.entityType}
                        </div>
                      </div>
                      <div className={`flex flex-col items-center ${typeCfg?.color ?? 'text-rose-500'}`}>
                        <div className="flex items-baseline gap-0.5">
                          <span className="text-2xl font-bold">{ann.years}</span>
                        </div>
                        <span className="text-xs text-foreground-400">años</span>
                      </div>
                    </div>

                    <p className="text-xs text-foreground-600 mb-3 line-clamp-2">{ann.description}</p>

                    {/* Actions */}
                    <div className="flex items-center gap-3 text-xs text-foreground-500">
                      <span className="flex items-center gap-1">
                        <i className="ri-heart-line text-rose-500"></i>
                        {ann.felicitaciones}
                      </span>
                      <span className="flex items-center gap-1">
                        <i className="ri-share-forward-line text-violet-500"></i>
                        {ann.compartidos}
                      </span>
                      <span className="flex items-center gap-1">
                        <i className="ri-chat-1-line text-sky-500"></i>
                        {ann.comentarios}
                      </span>
                    </div>
                  </div>
                );
              })}
            </div>
          </div>
        )}

        {/* Tab: Esta Semana */}
        {activeTab === 'semana' && (
          <div>
            <div className="space-y-3">
              {weekList.slice(0, 10).map((ann) => {
                const typeCfg = anniversaryTypeConfigs.find((t) => t.key === ann.entityType);
                return (
                  <div key={ann.id} className="bg-background-50 rounded-lg p-4 border border-background-200/70 hover:border-rose-200 transition-colors flex items-center gap-4">
                    <div className="w-12 h-12 rounded-xl overflow-hidden flex-shrink-0">
                      <img src={ann.entityPhoto} alt={ann.entityName} className="w-full h-full object-cover" />
                    </div>
                    <div className="flex-1 min-w-0">
                      <div className="flex items-center gap-2">
                        <span className="font-semibold text-sm text-foreground-950 truncate">{ann.entityName}</span>
                        <span className={`inline-flex items-center gap-1 px-1.5 py-0.5 rounded-full text-xs font-medium ${typeCfg?.bgColor ?? 'bg-background-100'} ${typeCfg?.color ?? 'text-foreground-600'}`}>
                          <i className={`${typeCfg?.icon ?? 'ri-question-line'} text-xs`}></i>
                          {typeCfg?.label}
                        </span>
                      </div>
                      <div className="text-xs text-foreground-500 mt-0.5 line-clamp-1">{ann.description}</div>
                    </div>
                    <div className="flex-shrink-0 text-right">
                      <div className={`text-lg font-bold ${typeCfg?.color ?? 'text-rose-500'}`}>{ann.years} años</div>
                      <div className="text-xs text-foreground-400 flex items-center gap-1 justify-end">
                        {ann.isToday ? (
                          <span className="text-rose-500 font-medium">¡Hoy!</span>
                        ) : (
                          <>
                            <i className="ri-calendar-line"></i>
                            <span>{ann.anniversaryDateFormatted}</span>
                            <span className="text-foreground-300">({ann.daysUntil === 1 ? 'mañana' : `en ${ann.daysUntil} días`})</span>
                          </>
                        )}
                      </div>
                    </div>
                  </div>
                );
              })}
            </div>
          </div>
        )}

        {/* Tab: Tipos */}
        {activeTab === 'tipos' && (
          <div>
            <div className="grid grid-cols-1 md:grid-cols-3 lg:grid-cols-5 gap-4 mb-6">
              {typeDist.map((dist) => {
                const cfg = anniversaryTypeConfigs.find((t) => t.key === dist.type);
                if (!cfg) return null;
                return (
                  <div key={dist.type} className={`bg-background-50 rounded-lg p-4 border ${cfg.borderColor} text-center hover:border-rose-200 transition-colors`}>
                    <div className={`w-12 h-12 mx-auto rounded-xl ${cfg.bgColor} flex items-center justify-center mb-3`}>
                      <i className={`${cfg.icon} text-xl ${cfg.color}`}></i>
                    </div>
                    <div className={`font-semibold text-sm ${cfg.color} mb-1`}>{cfg.label}</div>
                    <div className="text-xs text-foreground-500 mb-3">{cfg.description}</div>
                    <div className="grid grid-cols-3 gap-1 text-xs">
                      <div className="bg-rose-50 rounded-md py-1.5">
                        <div className="font-bold text-rose-600">{dist.today}</div>
                        <div className="text-foreground-400">Hoy</div>
                      </div>
                      <div className="bg-violet-50 rounded-md py-1.5">
                        <div className="font-bold text-violet-600">{dist.thisWeek}</div>
                        <div className="text-foreground-400">Semana</div>
                      </div>
                      <div className="bg-amber-50 rounded-md py-1.5">
                        <div className="font-bold text-amber-600">{dist.total}</div>
                        <div className="text-foreground-400">Total</div>
                      </div>
                    </div>
                  </div>
                );
              })}
            </div>

            {/* Most Celebrated */}
            <div className="bg-background-100 rounded-lg p-4 border border-background-200/70">
              <div className="text-sm font-semibold text-foreground-950 mb-3">Más Felicitados</div>
              <div className="space-y-2">
                {topCelebrated.map((ann, i) => {
                  const cfg = anniversaryTypeConfigs.find((t) => t.key === ann.entityType);
                  return (
                    <div key={ann.id} className="flex items-center gap-3 bg-background-50 rounded-md p-2.5">
                      <span className="text-xs font-bold text-foreground-400 w-5">#{i + 1}</span>
                      <img src={ann.entityPhoto} alt={ann.entityName} className="w-8 h-8 rounded-full object-cover" />
                      <div className="flex-1 min-w-0">
                        <div className="text-xs font-medium text-foreground-950 truncate">{ann.entityName}</div>
                        <div className={`text-xs ${cfg?.color ?? 'text-foreground-500'}`}>{ann.years} años · {cfg?.label}</div>
                      </div>
                      <div className="flex items-center gap-1 text-xs text-rose-500 font-medium">
                        <i className="ri-heart-fill"></i>
                        {ann.felicitaciones}
                      </div>
                    </div>
                  );
                })}
              </div>
            </div>
          </div>
        )}

        {/* Tab: Reglas */}
        {activeTab === 'reglas' && (
          <div>
            <div className="grid grid-cols-1 md:grid-cols-3 gap-4 mb-6">
              {anniversaryRules.map((rule) => (
                <div key={rule.id} className="bg-background-50 rounded-lg p-5 border border-background-200/70">
                  <div className={`w-10 h-10 rounded-full ${rule.bgColor} flex items-center justify-center mb-3`}>
                    <i className={`${rule.icon} text-lg ${rule.color}`}></i>
                  </div>
                  <div className="font-semibold text-sm text-foreground-950 mb-2">{rule.title}</div>
                  <div className="text-xs text-foreground-600 leading-relaxed">{rule.description}</div>
                </div>
              ))}
            </div>

            {/* Type counts */}
            <div className="bg-background-100 rounded-lg p-4 border border-background-200/70">
              <div className="text-sm font-semibold text-foreground-950 mb-3">Aniversarios por Tipo</div>
              <div className="grid grid-cols-2 md:grid-cols-5 gap-2">
                {typeDist.map((dist) => {
                  const cfg = anniversaryTypeConfigs.find((t) => t.key === dist.type);
                  return (
                    <div key={dist.type} className="text-center bg-background-50 rounded-lg p-3">
                      <i className={`${cfg?.icon ?? 'ri-question-line'} ${cfg?.color ?? 'text-foreground-500'} text-lg mb-1 block`}></i>
                      <div className="text-lg font-bold text-foreground-950">{dist.total}</div>
                      <div className="text-xs text-foreground-400">{cfg?.label}</div>
                    </div>
                  );
                })}
              </div>
            </div>
          </div>
        )}

        {/* CTA */}
        <div className="text-center mt-8">
          <a
            href="/aniversarios"
            className="inline-flex items-center gap-2 px-5 py-2.5 bg-rose-500 text-white rounded-full text-sm font-semibold hover:bg-rose-600 transition-colors whitespace-nowrap cursor-pointer"
          >
            <i className="ri-gift-line"></i>
            Ver todos los aniversarios
            <i className="ri-arrow-right-line"></i>
          </a>
        </div>

        {/* Final Rule */}
        <div className="mt-8 text-center">
          <p className="text-xs text-foreground-400 italic">Automático.</p>
        </div>
      </div>
    </section>
  );
}